import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { CheckCircle2, AlertCircle, Keyboard, ScanBarcode, LogOut } from 'lucide-react'
import { api } from '../../../../lib/api'
import { logout } from '../../../../utils/auth'

const FACTORY_CODES = ['CXB', 'CXC', 'CXD', 'CXE', 'CXF', 'CXM', 'EXT', 'FPC', 'FPL', 'FSP', 'MNT', 'RTP', 'ITC', 'CX']

/**
 * Pantalla PDA — recepción HU (SSCC) en Sala Mezcla.
 * URL: /pda/sala-mezcla?factory=CXC  o  /CXC/pda/sala-mezcla (rewrite a query)
 */
export default function SalaMezclaPDA() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const factory = (params.factory || searchParams.get('factory') || '').toUpperCase()

  const inputRef = useRef(null)
  const [barcode, setBarcode] = useState('')
  const [manualMode, setManualMode] = useState(false)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState(null) // { type, title, detail }
  const [lastOk, setLastOk] = useState(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [manualMode, feedback])

  const submit = useCallback(async (rawValue) => {
    const raw = (rawValue || '').trim()
    if (!raw || busy) return
    if (!factory || !FACTORY_CODES.includes(factory)) {
      setFeedback({ type: 'error', title: 'Fábrica no válida', detail: 'Abra /CXC/pda/sala-mezcla' })
      return
    }

    setBusy(true)
    setFeedback(null)
    try {
      const res = await api.post('/api/mixing-room/hu-receptions', {
        factory,
        barcode_raw: raw,
        manual_entry: manualMode,
        idempotency_key: `${factory}-${raw}-${Date.now()}`,
      })
      const data = res?.data
      setLastOk(data)
      setFeedback({
        type: 'ok',
        title: 'HU recepcionada',
        detail: data?.hu_sscc || raw,
      })
      setBarcode('')
      try {
        if (navigator.vibrate) navigator.vibrate(80)
      } catch { /* ignore */ }
    } catch (err) {
      const msg = err?.message || err?.error || String(err)
      const isDup = /DUPLICATE|ya recepcionada|Conflict|409/i.test(msg)
      setFeedback({
        type: 'error',
        title: isDup ? 'HU ya recepcionada' : 'Error al registrar',
        detail: msg,
      })
    } finally {
      setBusy(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [busy, factory, manualMode])

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submit(barcode)
    }
  }

  const handleLogout = async () => {
    await logout()
    window.location.href = '/login'
  }

  if (!factory) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto" />
          <p className="text-lg">Falta fábrica en la URL</p>
          <p className="text-gray-400 text-sm">Ejemplo: /CXC/pda/sala-mezcla</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      <header className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-blue-400">Auriga · PDA</p>
          <h1 className="text-lg font-semibold">{factory} / Sala Mezcla</h1>
          <p className="text-xs text-gray-400">Recepción de pallet (HU / SSCC)</p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700"
          title="Cerrar sesión"
        >
          <LogOut size={20} />
        </button>
      </header>

      <main className="flex-1 flex flex-col p-4 gap-4 max-w-lg mx-auto w-full">
        <div className={`rounded-xl border p-4 ${
          feedback?.type === 'ok'
            ? 'border-green-500/50 bg-green-500/10'
            : feedback?.type === 'error'
              ? 'border-red-500/50 bg-red-500/10'
              : 'border-gray-700 bg-gray-900'
        }`}
        >
          {feedback ? (
            <div className="flex gap-3 items-start">
              {feedback.type === 'ok'
                ? <CheckCircle2 className="text-green-400 shrink-0" />
                : <AlertCircle className="text-red-400 shrink-0" />}
              <div>
                <p className="font-semibold">{feedback.title}</p>
                <p className="text-sm text-gray-300 font-mono break-all">{feedback.detail}</p>
              </div>
            </div>
          ) : (
            <div className="flex gap-3 items-center text-gray-400">
              <ScanBarcode />
              <p className="text-sm">Escanee el código del pallet o use ingreso manual</p>
            </div>
          )}
        </div>

        <label className="block space-y-2">
          <span className="text-sm text-gray-400">
            {manualMode ? 'Ingreso manual de HU' : 'Código de barras (foco activo)'}
          </span>
          <input
            ref={inputRef}
            type="text"
            inputMode={manualMode ? 'numeric' : 'none'}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={busy}
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => setTimeout(() => inputRef.current?.focus(), 100)}
            className="w-full text-2xl font-mono px-4 py-4 rounded-xl bg-gray-900 border-2 border-blue-600 focus:outline-none focus:border-blue-400 text-white"
            placeholder={manualMode ? '000000001000966737' : 'Escanear…'}
          />
        </label>

        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy || !barcode.trim()}
            onClick={() => submit(barcode)}
            className="flex-1 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 font-semibold text-lg"
          >
            {busy ? 'Registrando…' : 'Registrar HU'}
          </button>
          <button
            type="button"
            onClick={() => {
              setManualMode((v) => !v)
              setBarcode('')
            }}
            className={`px-4 py-4 rounded-xl border font-medium flex items-center gap-2 ${
              manualMode
                ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                : 'border-gray-600 bg-gray-900 text-gray-300'
            }`}
          >
            <Keyboard size={20} />
            Manual
          </button>
        </div>

        {lastOk && (
          <div className="text-xs text-gray-500 space-y-1">
            <p>Última OK: <span className="font-mono text-gray-300">{lastOk.hu_sscc}</span></p>
            <p>{lastOk.received_by_email} · {new Date(lastOk.received_at).toLocaleString()}</p>
          </div>
        )}

        <Link
          to={`/lotes-trazabilidad?factory=${factory}`}
          className="text-center text-sm text-blue-400 hover:underline mt-auto py-4"
        >
          Ir a gestión (escritorio)
        </Link>
      </main>
    </div>
  )
}
